import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  parsePriceInput,
  positivePriceMessageParams,
} from '~/utils/moneyBounds';
import { currencyCodeToSymbolMap } from '~/utils/data';
import { cn } from '~/utils';

type SuggestedPriceInputProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  testId: string;
  currency?: string;
  unitLabel?: string;
  hint?: ReactNode;
  placeholder?: string;
  showErrorWhileTyping?: boolean;
};

export function SuggestedPriceInput({
  label,
  value,
  onChange,
  testId,
  currency,
  unitLabel,
  hint,
  placeholder,
  showErrorWhileTyping = false,
}: Readonly<SuggestedPriceInputProps>) {
  const { t } = useTranslation('cart');
  const inputId = useId();
  const unitId = useId();
  const hintId = useId();
  const errorId = useId();
  const [hasLeft, setHasLeft] = useState(false);
  const showError =
    (hasLeft || showErrorWhileTyping) && !parsePriceInput(value).valid;
  const describedBy = [
    unitLabel && unitId,
    hint && hintId,
    showError && errorId,
  ].filter(Boolean);

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={inputId} className="text-xs font-medium text-neut-sec">
        {label}
      </label>
      <div
        className={cn(
          'flex items-center gap-1 rounded-md border bg-neut-bg px-3 focus-within:ring-2 focus-within:ring-prim',
          showError ? 'border-error-500' : 'border-neut-border'
        )}
      >
        <span aria-hidden="true" className="text-sm text-neut-sec">
          {(currency ? currencyCodeToSymbolMap[currency] : undefined) ?? '$'}
        </span>
        <input
          id={inputId}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          required
          placeholder={placeholder}
          aria-invalid={showError || undefined}
          aria-describedby={describedBy.join(' ') || undefined}
          data-testid={testId}
          className="w-full min-w-0 bg-transparent py-2 text-sm focus:outline-none"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onBlur={() => setHasLeft(true)}
        />
        {unitLabel && (
          <span
            id={unitId}
            className="shrink-0 text-xs text-neut-sec"
            data-testid={`${testId}-unit`}
          >
            / {unitLabel}
          </span>
        )}
      </div>
      {hint && (
        <div id={hintId} className="text-xs text-neut-sec">
          {hint}
        </div>
      )}
      {showError && (
        <p
          id={errorId}
          className="text-xs text-error-500"
          data-testid={`${testId}-error`}
        >
          {t('cart.suggested_price_invalid', positivePriceMessageParams)}
        </p>
      )}
    </div>
  );
}
